import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { buildLessonTranscript, type LessonTranscript } from '../lib/lessonTranscript.js';
import { useLessonPreview } from '../lib/useLessonPreview.js';
import type { WorkItemSummary } from '../types/workflow.js';
import type { LessonPreview } from '../types/lessonPreview.js';
import { stepName, QueueTranscriptContext, RawStoredField, ResponseBlock, StepBlock, WarningList } from './TranscriptBlocks.js';

type Append = Extract<LessonPreview, { kind: 'append' }>;

export function AppendPreview({ item, courseId, onLoaded }: {
  item: WorkItemSummary;
  courseId: string;
  onLoaded?: (loaded: boolean) => void;
}) {
  const state = useLessonPreview(item.workItemId);
  const preview = state.status === 'ready' && state.preview.kind === 'append' ? state.preview : null;
  const loaded = !!preview && preview.state === 'ready' && !preview.lessonChangedSinceImport && preview.projected !== null;
  useEffect(() => { onLoaded?.(loaded); }, [loaded, onLoaded]);
  // Plain approval ships the suggestion, even if a cancelled item still has a reviewer patch.
  const envelope = item.suggestedPatch._append;
  const addition = envelope && typeof envelope === 'object' && !Array.isArray(envelope)
    ? envelope as Record<string, unknown> : {};
  const live = preview?.live ? buildLessonTranscript(preview.live, 'entity') : null;
  const projected = preview?.state === 'ready' && preview.projected
    ? buildLessonTranscript(preview.projected, 'entity') : null;
  const newContent = buildLessonTranscript({ steps: addition.steps, responses: addition.responses }, 'entity');
  // A partial document deliberately omits the existing splice destination. Do not call that a
  // broken route; keep warnings for every other missing destination.
  const continuation = typeof addition.continues_to === 'number' ? addition.continues_to : undefined;
  const newStepIds = preview?.newStepIds.length ? preview.newStepIds : newContent.steps.map((step) => step.stepId);
  const newResponseIds = preview?.newResponseIds.length ? preview.newResponseIds
    : [...new Set([...newContent.steps.flatMap((step) => step.responses), ...newContent.orphanResponses].map((response) => response.responseId))];
  const links = preview?.links ?? [];
  const destinations = projected ?? newContent;
  const rerouted = (object: 'response' | 'step') => new Map(links.filter((link) => link.object === object)
    .map((link) => [link.id, stepName(link.after, destinations)]));
  const error = state.status === 'error' || (state.status === 'ready' && !preview)
    ? 'Could not load the lesson preview. The proposed steps below have not been checked against the live lesson.'
    : preview?.state === 'ready' && !projected
      ? 'The preview returned no projected lesson. The proposed steps below have not been checked against the live lesson.' : null;

  if (state.status === 'loading') return <section data-testid="append-preview">Loading lesson preview…</section>;
  return (
    <QueueTranscriptContext.Provider value={{ continuation, transcript: { ...newContent, steps: [...newContent.steps, ...(live?.steps ?? [])] } }}>
      <section className="space-y-4" data-testid="append-preview">
        <h3 className="text-lg font-bold text-slate-900">
          <Link className="text-blue-700 underline" to={`/courses/${encodeURIComponent(courseId)}/entities/${preview?.lessonId ?? item.targetId}/transcript`}>
            Steps added to lesson #{preview?.lessonId ?? item.targetId} {preview?.lessonTitle}
          </Link>
        </h3>
        {error ? <div role="alert" className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900">
          <p>{error}</p><button type="button" className="mt-2 font-bold underline" onClick={state.retry}>Retry</button>
        </div> : null}
        {preview?.state === 'refused' || preview?.lessonChangedSinceImport ? (
          <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900" data-testid="append-preview-refused" role="alert">
            <p>The release would refuse this addition as it stands; reject it so it can be redrafted against the lesson as it is now.</p>
            {preview.state === 'refused' ? <p>{preview.refusal?.message ?? 'This addition can no longer be applied to the live lesson.'}</p> : null}
            {preview.lessonChangedSinceImport ? <p>The lesson has changed since this addition was drafted; it will be refused at release and needs a redraft.</p> : null}
          </div>
        ) : null}
        <div className="space-y-2 text-sm text-slate-800">
          <h4 className="font-bold">What changes</h4>
          <p>{newStepIds.length} new {newStepIds.length === 1 ? 'step' : 'steps'}, {newResponseIds.length} new {newResponseIds.length === 1 ? 'option' : 'options'}</p>
          <ul className="space-y-2">
            {links.map((link, index) => <li key={`${link.object}-${link.id}-${index}`}>{routeSentence(link, live, destinations)}</li>)}
          </ul>
          {preview?.summary ? (
            <div data-testid="append-preview-summary">
              <p className="whitespace-pre-wrap rounded-t-lg bg-red-50 p-3 text-red-900">Summary before: {preview.summary.before}</p>
              <p className="whitespace-pre-wrap rounded-b-lg bg-green-50 p-3 text-green-900">Summary after: {preview.summary.after}</p>
            </div>
          ) : null}
        </div>
        {projected ? (
          <QueueTranscriptContext.Provider value={{ transcript: projected }}>
            <Transcript transcript={projected} title="Lesson with the addition" newStepIds={newStepIds} newResponseIds={newResponseIds} reroutedResponses={rerouted('response')} reroutedSteps={rerouted('step')} />
          </QueueTranscriptContext.Provider>
        ) : (
          <>
            {live ? <QueueTranscriptContext.Provider value={{ transcript: live }}><Transcript transcript={live} title="Live lesson — addition not applied" /></QueueTranscriptContext.Provider> : <p>The live lesson is unavailable.</p>}
            <Transcript transcript={newContent} title="Proposed new steps — listed separately, not applied" newStepIds={newStepIds} newResponseIds={newResponseIds} />
          </>
        )}
      </section>
    </QueueTranscriptContext.Provider>
  );
}

function routeSentence(link: Append['links'][number], live: LessonTranscript | null, projected: LessonTranscript): string {
  const destination = `${stepName(link.after, projected)} instead of ${stepName(link.before, live)}.`;
  if (link.object === 'step') return `From ${stepName(link.id, live)}, the route now leads to ${destination}`;
  const owners = live?.steps.filter((step) => step.responses.some((response) => response.responseId === link.id)) ?? [];
  const response = owners[0]?.responses.find((option) => option.responseId === link.id)
    ?? live?.orphanResponses.find((option) => option.responseId === link.id);
  const text = response ? ` '${response.text}'` : '';
  const after = owners.length ? ` after ${owners.map((step) => stepName(step.stepId, live)).join('; ')}` : '';
  return `Option ${link.id}${text}${after} now leads to ${destination}`;
}

function Transcript({ transcript, title, newStepIds = [], newResponseIds = [], reroutedResponses = new Map(), reroutedSteps = new Map() }: {
  transcript: LessonTranscript;
  title: string;
  newStepIds?: number[];
  newResponseIds?: number[];
  reroutedResponses?: ReadonlyMap<number, string>;
  reroutedSteps?: ReadonlyMap<number, string>;
}) {
  return (
    <section className="space-y-3">
      <h4 className="font-bold text-slate-900">{title}</h4>
      {transcript.summary !== null ? <p className="whitespace-pre-wrap text-sm">{transcript.summary}</p> : null}
      <RawStoredField label="summary" raw={transcript.rawSummary} />
      {transcript.rcText !== null ? <p className="whitespace-pre-wrap text-sm">{transcript.rcText}</p> : null}
      <RawStoredField label="reference text" raw={transcript.rawRcText} />
      <WarningList warnings={transcript.warnings} />
      {transcript.steps.map((step, index) => (
        <StepBlock key={`${step.stepId}-${index}`} step={step} testId={`append-preview-step-${step.stepId}`} isNew={newStepIds.includes(step.stepId)} newResponseIds={newResponseIds} reroutedResponses={reroutedResponses} reroutedTo={reroutedSteps.get(step.stepId)} />
      ))}
      {transcript.orphanResponses.length ? (
        <ol className="space-y-3">{transcript.orphanResponses.map((response, index) => (
          <ResponseBlock key={`${response.responseId}-${index}`} response={response} orphan isNew={newResponseIds.includes(response.responseId)} reroutedTo={reroutedResponses.get(response.responseId)} />
        ))}</ol>
      ) : null}
    </section>
  );
}
